import api from '../../../core/api/client';

const PROJECTS_BASE_PATH = '/projects';

const FORBIDDEN_PAYLOAD_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const toApiError = (error) => {
  if (error?.response) {
    const responseData = error.response.data;
    const message =
      responseData?.detail ||
      responseData?.message ||
      responseData?.error ||
      error.message ||
      'Request failed';

    return {
      status: error.response.status,
      message,
      data: responseData,
    };
  }

  return {
    status: 0,
    message: error?.message || 'Unexpected error',
    data: null,
  };
};

const normalizeProjectId = (projectId) => {
  if (projectId === null || projectId === undefined) {
    throw new Error('project_id is required');
  }

  const normalized = String(projectId).trim();
  if (!normalized) {
    throw new Error('project_id is required');
  }

  return encodeURIComponent(normalized);
};

const sanitizePayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (FORBIDDEN_PAYLOAD_KEYS.has(key) || value === undefined) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

const projectPath = (projectId) => `${PROJECTS_BASE_PATH}/${normalizeProjectId(projectId)}`;

const withApiErrorHandling = async (request) => {
  try {
    return await request();
  } catch (error) {
    throw toApiError(error);
  }
};

const normalizeProject = (project) => {
  if (!project || typeof project !== 'object') {
    return project;
  }

  const normalizedTeam = Array.isArray(project.team)
    ? project.team.map((member) => ({
        ...member,
        user_id: member?.user_id || member?.id,
      }))
    : [];

  return {
    ...project,
    members: Array.isArray(project.members) && project.members.length > 0 ? project.members : normalizedTeam,
    project_manager_id: project.project_manager_id || project.project_manager,
  };
};

const normalizeProjectListResponse = (payload) => {
  const projectList = Array.isArray(payload) ? payload : payload?.projects;
  const projects = Array.isArray(projectList) ? projectList.map(normalizeProject) : [];

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      ...payload,
      projects,
    };
  }

  return { projects };
};

export const projectService = {
  async getAllProjects() {
    return withApiErrorHandling(async () => {
      const response = await api.get(PROJECTS_BASE_PATH);
      return normalizeProjectListResponse(response.data);
    });
  },

  async getProjects() {
    return this.getAllProjects();
  },

  async getProjectById(projectId) {
    return withApiErrorHandling(async () => {
      const response = await api.get(projectPath(projectId));
      return normalizeProject(response.data);
    });
  },

  // Alias kept for API contract readability: GET /projects/{project_id}
  async getProject(projectId) {
    return this.getProjectById(projectId);
  },

  async createProject(projectData) {
    return withApiErrorHandling(async () => {
      const payload = sanitizePayload(projectData);
      const response = await api.post(PROJECTS_BASE_PATH, payload);
      return normalizeProject(response.data);
    });
  },

  async updateProject(projectId, updates) {
    return withApiErrorHandling(async () => {
      const payload = sanitizePayload(updates);
      const response = await api.patch(projectPath(projectId), payload);
      return normalizeProject(response.data);
    });
  },

  // Alias kept for API contract readability: PATCH /projects/{project_id}
  async patchProject(projectId, updates) {
    return this.updateProject(projectId, updates);
  },

  async deleteProject(projectId) {
    return withApiErrorHandling(async () => {
      const response = await api.delete(projectPath(projectId));
      return response.data;
    });
  },

  // Alias kept for API contract readability: DELETE /projects/{project_id}
  async removeProject(projectId) {
    return this.deleteProject(projectId);
  },

  async updateProjectStatus(projectId, statusOrPayload) {
    return withApiErrorHandling(async () => {
      const payload =
        statusOrPayload && typeof statusOrPayload === 'object'
          ? sanitizePayload(statusOrPayload)
          : { status: statusOrPayload };
      const response = await api.patch(`${projectPath(projectId)}/status`, payload);
      return response.data;
    });
  },

  // Alias kept for API contract readability: PATCH /projects/{project_id}/status
  async changeProjectStatus(projectId, statusOrPayload) {
    return this.updateProjectStatus(projectId, statusOrPayload);
  },

  async updatePresentable(projectId, presentable) {
    return withApiErrorHandling(async () => {
      // API expects { presentable: { sectionKey: ["docId", ...] } }
      const payload = { presentable: sanitizePayload(presentable) };
      const response = await api.patch(`${projectPath(projectId)}/presentable`, payload);
      return response.data;
    });
  },

  // Explicit alias for API contract readability: PATCH /projects/{project_id}/presentable
  async updateProjectPresentable(projectId, presentablePayload) {
    return this.updatePresentable(projectId, presentablePayload);
  },

  async recomputeProgress(projectId) {
    return withApiErrorHandling(async () => {
      const response = await api.post(`${projectPath(projectId)}/recompute-progress`);
      return response.data;
    });
  },

  async recomputeProjectProgress(projectId) {
    return this.recomputeProgress(projectId);
  },

  async updateSpecifications(projectId, specifications) {
    return withApiErrorHandling(async () => {
      const response = await api.patch(`${projectPath(projectId)}/specifications`, { specifications });
      return response.data;
    });
  },

  async updateLaunchStrategy(projectId, launchStrategy) {
    return withApiErrorHandling(async () => {
      const response = await api.patch(`${projectPath(projectId)}/launch-strategy`, { launchStrategy });
      return response.data;
    });
  },

  async addSocialPost(projectId, postData) {
    return withApiErrorHandling(async () => {
      const response = await api.post(`${projectPath(projectId)}/launch-strategy/social-posts`, postData);
      return response.data;
    });
  },

  async deleteSocialPost(projectId, platform, postId) {
    return withApiErrorHandling(async () => {
      const response = await api.delete(
        `${projectPath(projectId)}/launch-strategy/social-posts/${encodeURIComponent(platform)}/${encodeURIComponent(postId)}`
      );
      return response.data;
    });
  },
};
